import { useState } from "react";
import { Plus, FileDown, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCollection } from "@/hooks/useCollection";
import { useAuth } from "@/context/AuthContext";
import { inr, api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, StatCard } from "@/components/crm/common";
import { DataTable } from "@/components/crm/DataTable";
import { FilterBar, applyFilters } from "@/components/crm/FilterBar";
import { FormDialog } from "@/components/crm/FormDialog";
import { FileBox } from "@/components/crm/Files";
import { amt } from "@/components/crm/Expenses";

const STATUS = ["Quote requested", "Negotiation", "Confirmed"];

function StatementButton({ v }) {
  const [busy, setBusy] = useState(false);
  const download = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const res = await api.get(`/vendors/${v.id}/statement`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "vendor-statement.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = name;
      a.click();
    } catch (err) {
      const text = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : err.response?.data?.detail;
      toast.error(formatApiErrorDetail(text) || "Could not download statement");
    }
    setBusy(false);
  };
  return (
    <Button data-testid={`vendor-statement-${v.id}`} size="sm" variant="ghost" className="h-7 px-2 text-violet-700" disabled={busy} onClick={download}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1" />}Statement
    </Button>
  );
}

function EmailStatementButton({ v, onSent }) {
  const [busy, setBusy] = useState(false);
  if (!v.email) return <span data-testid={`vendor-statement-email-missing-${v.id}`} className="px-2 text-[11px] text-zinc-400">No email</span>;
  const send = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Email the statement PDF to ${v.email}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/vendors/${v.id}/statement/email`);
      toast.success(`Statement emailed to ${data.statement_sent_to}`, { description: `${data.bills} bill(s) included` });
      onSent();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Button data-testid={`vendor-statement-email-${v.id}`} size="sm" variant="ghost" className="h-7 px-2 text-violet-700" disabled={busy} onClick={send} title={v.statement_sent_at ? `Last sent ${v.statement_sent_at.slice(0, 10)} by ${v.statement_sent_by}` : undefined}>
      {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}Email
    </Button>
  );
}

export default function Vendors() {
  const { can } = useAuth();
  const vendors = useCollection("vendors");
  const { items: projects } = useCollection("projects");
  const { items: bills } = useCollection("vendor_expenses");
  const [search, setSearch] = useState("");
  const [proj, setProj] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState(null);
  const pName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const billsFor = (v) => bills.filter((b) => b.project_id === v.project_id && [b.company, b.vendor_name].some((x) => x && x.toLowerCase() === (v.name || "").toLowerCase()));
  const rows = applyFilters(vendors.items, search, ["name", "service", "contact", "company"], [{ key: "project_id", value: proj }, { key: "status", value: status }]);
  const editable = can("Vendors", "edit") || can("Execution", "edit");
  const canEmail = can("VendorExpenses", "edit");

  const save = async (f) => !!(f.id ? await vendors.update(f.id, f) : await vendors.create(f, "Vendor added"));

  return (
    <>
      <PageHeader num="14" title="Vendor Management" subtitle="Vendor details, quotations and bills across every project.">
        {(can("Vendors", "create") || can("Execution", "create")) && <Button data-testid="add-vendor-button" onClick={() => setForm({ status: "Quote requested" })}><Plus className="h-4 w-4 mr-1" />Add vendor</Button>}
      </PageHeader>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="vendors-count" label="Vendors" value={vendors.items.length} />
        <StatCard testid="vendors-confirmed" label="Confirmed" value={vendors.items.filter((v) => v.status === "Confirmed").length} tone="text-emerald-600" />
        <StatCard testid="vendors-billed" label="Total billed" value={inr(bills.reduce((s, b) => s + amt(b), 0))} />
        <StatCard testid="vendors-outstanding" label="Approved, unpaid" value={inr(bills.filter((b) => b.approval_status === "Approved" && b.payment_status !== "Paid").reduce((s, b) => s + amt(b), 0))} tone="text-violet-700" />
      </div>
      <Panel title="Vendor directory">
        <FilterBar testid="vendors" search={search} setSearch={setSearch} filters={[
          { key: "project", label: "projects", value: proj, set: setProj, options: projects.map((p) => ({ value: p.id, label: p.name })) },
          { key: "status", label: "statuses", value: status, set: setStatus, options: STATUS },
        ]} />
        <DataTable testid="vendors-table" rows={rows} onRowClick={(v) => editable && setForm(v)} columns={[
          { key: "name", label: "Vendor", render: (v) => <div><div className="font-semibold text-zinc-900">{v.name}</div><div className="text-xs text-zinc-500">{v.company || v.service}</div></div> },
          { key: "project_id", label: "Project", render: (v) => pName(v.project_id) },
          { key: "service", label: "Service" },
          { key: "contact", label: "Contact", render: (v) => <div className="text-xs"><div>{v.contact || "—"}</div><div className="text-zinc-400">{[v.phone, v.email].filter(Boolean).join(" · ")}</div></div> },
          { key: "gst_no", label: "GSTIN" },
          { key: "status", label: "Status", render: (v) => <StatusBadge value={v.status} /> },
          { key: "bills", label: "Bills", render: (v) => { const b = billsFor(v); return <div className="text-xs"><div className="font-semibold text-zinc-800">{inr(b.reduce((s, x) => s + amt(x), 0))}</div><div className="text-zinc-400">{b.length} bill(s)</div></div>; } },
          { key: "quotes", label: "Quotations", render: (v) => <div onClick={(e) => e.stopPropagation()} className="max-w-[180px]"><FileBox compact testid={`vendor-quotes-${v.id}`} entityType="vendor" entityId={v.id} category="quotation" canUpload={editable} /></div> },
          { key: "statement", label: "Statement", render: (v) => can("Budget", "view") ? <div className="flex flex-col items-start"><StatementButton v={v} />{canEmail && <EmailStatementButton v={v} onSent={vendors.reload} />}{v.statement_sent_at && <span data-testid={`vendor-statement-sent-${v.id}`} className="px-2 text-[10px] text-zinc-400">Sent {v.statement_sent_at.slice(0, 10)}</span>}</div> : <span className="text-xs text-zinc-400">Finance only</span> },
        ]} />
      </Panel>
      <FormDialog testid="vendor-form" open={!!form} onOpenChange={(o) => !o && setForm(null)} title={form?.id ? "Edit vendor" : "Add vendor"} initial={form} onSubmit={save}
        fields={[{ name: "project_id", label: "Project", type: "select", options: projects.map((p) => ({ value: p.id, label: p.name })), required: true },
          { name: "name", label: "Vendor name", required: true }, { name: "company", label: "Company" }, { name: "service", label: "Service / task", required: true },
          { name: "contact", label: "Contact person" }, { name: "phone", label: "Phone" }, { name: "email", label: "Email", type: "email" }, { name: "gst_no", label: "GSTIN" },
          { name: "beneficiary_name", label: "Bank beneficiary name" }, { name: "bank_name", label: "Bank name" }, { name: "account_no", label: "Account number" }, { name: "ifsc", label: "IFSC code" },
          { name: "status", label: "Status", type: "select", options: STATUS }]} />
    </>
  );
}
